import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Icon } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { adminApi } from "../api";
import type { IconName } from "@nelurio/ui";
import type { AdminUserRow } from "../types";

interface SearchSection {
  title: string;
  items: Array<{ label: string; sub?: string; to: string; icon: IconName }>;
}

/** Wix-Studio-style command palette (Ctrl/Cmd+K): sections + users, every
 *  result is a real jump target. Users come from the live /users list. */
export function GlobalSearchOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<AdminUserRow[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    const timer = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open || users) return;
    let cancelled = false;
    adminApi<AdminUserRow[]>("/users?limit=100")
      .then((result) => { if (!cancelled) setUsers(result); })
      .catch(() => { if (!cancelled) setUsers([]); });
    return () => { cancelled = true; };
  }, [open, users]);

  const sections: SearchSection[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    const navItems: SearchSection = {
      title: t("admin.search.sections"),
      items: [
        { label: t("admin.overview"), to: "/", icon: "chart" as IconName },
        { label: t("admin.users"), to: "/users", icon: "users" as IconName },
        { label: t("admin.subscriptions"), to: "/subscriptions", icon: "credit-card" as IconName },
        { label: t("admin.products"), to: "/products", icon: "library" as IconName },
        { label: t("admin.feedback"), to: "/feedback", icon: "message" as IconName },
        { label: t("admin.website"), to: "/website", icon: "globe" as IconName },
        { label: t("admin.privacy"), to: "/privacy", icon: "shield" as IconName },
        { label: t("admin.system"), to: "/system", icon: "server" as IconName }
      ].filter((item) => q === "" || item.label.toLowerCase().includes(q))
    };
    const userItems: SearchSection = {
      title: t("admin.search.users"),
      items: (users ?? [])
        .filter((user) => q === "" || user.email.toLowerCase().includes(q) || (user.displayName ?? "").toLowerCase().includes(q))
        .slice(0, 6)
        .map((user) => ({
          label: user.displayName || user.email,
          sub: user.email,
          to: `/users?focus=${encodeURIComponent(user.email)}`,
          icon: "users"
        }))
    };
    return [navItems, ...(users === null ? [] : [userItems])].filter((section) => section.items.length > 0);
  }, [query, users, t]);

  if (!open) return null;
  const go = (to: string) => { onClose(); navigate(to); };

  return (
    <div className="admin-modal admin-search" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="admin-modal__card admin-search__card" role="dialog" aria-modal="true" aria-label={t("admin.search.aria")}>
        <div className="admin-search__bar">
          <Icon name="search" size={18} />
          <input
            ref={inputRef}
            value={query}
            placeholder={t("admin.search.placeholder")}
            aria-label={t("admin.search.aria")}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") onClose();
              if (event.key === "Enter") {
                const first = sections[0]?.items[0];
                if (first) go(first.to);
              }
            }}
          />
          <button type="button" className="admin-search__esc" onClick={onClose} aria-label={t("common.close")}>Esc</button>
        </div>
        <div className="admin-search__results">
          {sections.map((section) => (
            <div key={section.title} className="admin-search__section">
              <span className="admin-search__section-title">{section.title}</span>
              {section.items.map((item) => (
                <button key={item.to + item.label} type="button" className="admin-search__row" onClick={() => go(item.to)}>
                  <Icon name={item.icon} size={16} />
                  <span className="admin-search__row-label">{item.label}</span>
                  {item.sub && <small className="admin-wrap">{item.sub}</small>}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
